// Sanitiser for the vendor HTML the affiliate portal previews.
//
// The read-only rich-text editor these previews replaced never rendered the
// raw markup: it parsed the HTML into its own document model and re-rendered a
// normalised subset, which quietly dropped scripts and event handlers. A plain
// `v-html` does not, so the same guarantee has to be made explicitly here.
//
// This is deliberately a small, targeted denylist rather than a general
// purpose sanitiser (DOMPurify is not a dependency of this app): promo assets
// are marketing HTML — headings, paragraphs, links, images, tables, styled
// spans and the occasional video embed — and everything that can execute is
// removed.
//
// `sanitiseHtml(html, { scope })` additionally rewrites the asset's own
// stylesheet so its rules can only match inside the preview well that carries
// the `scope` class. Without that, one vendor's `body { background: #000 }`
// repaints the whole affiliate portal.

const MATHML_NAMESPACE = 'http://www.w3.org/1998/Math/MathML';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

// Elements that either execute code, re-point the document, or carry markup
// the two HTML parsers below disagree about.
//
// `noscript` and `template` are the parser-differential pair: `DOMParser`
// parses with scripting *off*, where `<noscript>` holds real elements and a
// `</noscript>` inside an attribute value is just text; `innerHTML` — which is
// what `v-html` runs — parses with scripting *on*, where `<noscript>` is raw
// text that the same `</noscript>` closes, releasing whatever followed it as
// live markup. `<template>` is the mirror image: its content is a separate
// document fragment that `querySelectorAll` never walks, so nothing inside it
// is ever inspected. Neither belongs in a promo asset, so both go, subtree and
// all, rather than being reasoned about.
const BLOCKED_ELEMENTS = [
  'script', 'object', 'embed', 'base', 'meta', 'link',
  'noscript', 'template',
  // SMIL: `<set attributeName="onload" to="alert(1)">` and friends write
  // attributes onto their parent after the sanitiser has looked at it.
  'animate', 'set', 'animatetransform', 'animatemotion',
];

// A `<form>` is not dangerous in itself, and a legitimate opt-in ad is built
// out of one, so it is unwrapped (kept content, dropped element) rather than
// removed: an affiliate previewing a squeeze-page ad still sees the field and
// the button, and nothing can be submitted from a preview that has no form.
const UNWRAPPED_ELEMENTS = ['form'];

// MathML is only ever present in a promo asset as the first half of an mXSS
// payload: `<math><mtext>` switches the parser into foreign content, where the
// text inside `<style>`/`<mglyph>` is re-parsed as markup on the next pass. The
// namespace check below is the real guard; these names are the belt to its
// braces for parsers that do not report a namespace.
const MATHML_ELEMENTS = [
  'math', 'mtext', 'mglyph', 'mi', 'mo', 'mn', 'ms', 'maction',
  'malignmark', 'annotation-xml', 'semantics', 'mrow', 'mspace',
];

// Attributes that carry a URL, and so can carry `javascript:` too.
const URL_ATTRIBUTES = ['href', 'src', 'xlink:href', 'action', 'formaction', 'poster', 'background'];

// `srcset` carries a comma-separated *list* of URLs, each with an optional
// descriptor, so it is checked candidate by candidate.
const SRCSET_ATTRIBUTES = ['srcset', 'imagesrcset'];

const DANGEROUS_SCHEME = /^(?:javascript:|vbscript:|data:text\/html)/;

// Every surviving embed runs boxed. `allow-scripts` and `allow-same-origin`
// are what a YouTube/Vimeo player needs to play at all, `allow-presentation`
// is its fullscreen button and `allow-popups` its "watch on ..." link.
// `allow-top-navigation` is deliberately absent: an embed a vendor pasted
// cannot navigate the affiliate's portal away from under them.
const IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-presentation allow-popups';

const MAX_PASSES = 3;

// CSSOM rule types. `CSSRule.STYLE_RULE` and friends are not reliably exposed
// on the rule objects every engine hands back, so the numbers are named here.
const STYLE_RULE = 1;
const FONT_FACE_RULE = 5;
const MEDIA_RULE = 4;
const KEYFRAMES_RULE = 7;
const SUPPORTS_RULE = 12;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function localNameOf(element) {
  const name = element.localName || element.tagName || '';
  return String(name).toLowerCase();
}

function remove(element) {
  if (element.parentNode) {
    element.parentNode.removeChild(element);
  }
}

function unwrap(element) {
  const parent = element.parentNode;

  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  parent.removeChild(element);
}

function isInNamespace(element, namespace) {
  if (element.namespaceURI === namespace) {
    return true;
  }

  // `closest` is not available on foreign-content elements in every engine.
  let node = element.parentNode;

  while (node && node.nodeType === 1) {
    if (node.namespaceURI === namespace) {
      return true;
    }

    node = node.parentNode;
  }

  return false;
}

// The DOM has already decoded entities by the time we read an attribute, so a
// `&#9;`, a raw tab, a newline or a NUL between the scheme's letters all show
// up here as the character itself. Browsers ignore them when they resolve the
// URL, so they have to be ignored before the scheme is compared too. U+FFFD is
// in the set because that is what the HTML parser turns a NUL into.
function normaliseUrl(value) {
  return String(value || '').replace(/[\x00-\x20\uFFFD]+/g, '').toLowerCase();
}

function isDangerousUrl(value) {
  return DANGEROUS_SCHEME.test(normaliseUrl(value));
}

function isDangerousSrcset(value) {
  return String(value || '')
    .split(',')
    .some(candidate => isDangerousUrl(String(candidate).trim().split(/\s+/)[0]));
}

// `expression(...)` executes in old IE and `url(javascript:...)` in more
// engines than one would hope. Both only run as a *value*, so the search is
// limited to what follows the colon of a declaration: `.expression (x) { … }`
// is an ordinary class selector, and `content: 'expression('` is an ordinary
// string. Whitespace, control characters and case are normalised away first so
// that `url( 'javascript:…' )` is still caught.
function isDangerousValue(value) {
  const compact = String(value || '').toLowerCase().replace(/[\x00-\x20\uFFFD]+/g, '');

  if (/url\(["']?(?:javascript|vbscript):/.test(compact)) {
    return true;
  }

  // Quoted strings cannot execute, so they are blanked before the bare
  // `expression(` search.
  const unquoted = compact.replace(/"[^"]*"/g, '""').replace(/'[^']*'/g, "''");

  return unquoted.indexOf('expression(') !== -1;
}

// A declaration list: what a `style` attribute holds, and what sits between
// the braces of a rule.
function hasDangerousDeclaration(declarations) {
  return String(declarations || '').split(';').some(declaration => {
    const colon = declaration.indexOf(':');
    return colon !== -1 && isDangerousValue(declaration.slice(colon + 1));
  });
}

// A stylesheet: only the innermost brace bodies are declaration lists, so the
// selectors and at-rule preludes around them are never searched.
function hasDangerousBlock(css) {
  const blocks = css.match(/\{[^{}]*\}/g) || [];

  return blocks.some(block => hasDangerousDeclaration(block.slice(1, -1)));
}

function hasDangerousStylesheet(css) {
  const text = String(css || '').replace(/\/\*[\s\S]*?\*\//g, '');

  if (hasDangerousBlock(text)) {
    return true;
  }

  // `p { content: "}"; background: url(javascript:…) }` ends a block early for
  // the scan above and nowhere else: the CSS parser reads that `}` as part of
  // the string. Scanning again with the quoted strings removed puts the real
  // block boundaries back — and drops nothing that could execute, because a
  // quoted string cannot.
  return hasDangerousBlock(text.replace(/"[^"]*"/g, '').replace(/'[^']*'/g, ''));
}

function alreadyScoped(selector, scopeSelector) {
  if (selector.indexOf(scopeSelector) !== 0) {
    return false;
  }

  const next = selector.charAt(scopeSelector.length);

  return next === '' || !/[\w-]/.test(next);
}

// `body`, `html` and `:root` all mean "the whole page" — inside a preview that
// is the well itself, so those leading tokens become the scope rather than
// being prefixed by it (`.scope body p` would match nothing).
function scopeOneSelector(selector, scopeSelector) {
  const trimmed = String(selector || '').trim();

  if (!trimmed || alreadyScoped(trimmed, scopeSelector)) {
    return trimmed;
  }

  let rest = trimmed;
  let matched = false;
  let child = false;

  for (;;) {
    const token = /^(?:html|body|:root)(?![\w-])/i.exec(rest);

    if (!token) {
      break;
    }

    matched = true;
    rest = rest.slice(token[0].length);

    const combinator = /^(?:\s*>\s*|\s+)/.exec(rest);

    if (!combinator) {
      break;
    }

    child = combinator[0].indexOf('>') !== -1;
    rest = rest.slice(combinator[0].length);
  }

  if (!matched) {
    return `${scopeSelector} ${trimmed}`;
  }

  if (!rest) {
    return scopeSelector;
  }

  // `body.dark` is the well itself carrying that class, not a descendant.
  if (/^[.#:[]/.test(rest)) {
    return `${scopeSelector}${rest}`;
  }

  return child ? `${scopeSelector} > ${rest}` : `${scopeSelector} ${rest}`;
}

// `:not(h1, h2)` and `[title="a,b"]` both carry a comma that does not separate
// two selectors, so the list is split only where the comma sits outside every
// bracket and quote.
function splitTopLevel(value) {
  const parts = [];
  let current = '';
  let depth = 0;
  let quote = '';

  for (let index = 0; index < value.length; index += 1) {
    const character = value.charAt(index);

    if (character === '\\') {
      current += character + value.charAt(index + 1);
      index += 1;
    } else if (quote) {
      current += character;

      if (character === quote) {
        quote = '';
      }
    } else if (character === '"' || character === "'") {
      quote = character;
      current += character;
    } else if (character === '(' || character === '[') {
      depth += 1;
      current += character;
    } else if (character === ')' || character === ']') {
      depth = Math.max(0, depth - 1);
      current += character;
    } else if (character === ',' && depth === 0) {
      parts.push(current);
      current = '';
    } else {
      current += character;
    }
  }

  parts.push(current);

  return parts;
}

function scopeSelectorList(selectorText, scopeSelector) {
  return splitTopLevel(String(selectorText || ''))
    .map(part => scopeOneSelector(part, scopeSelector))
    .filter(Boolean)
    .join(', ');
}

function serialiseRules(rules, scopeSelector) {
  return Array.prototype.map.call(rules || [], rule => serialiseRule(rule, scopeSelector))
    .filter(Boolean)
    .join(' ');
}

function serialiseRule(rule, scopeSelector) {
  if (!rule) {
    return '';
  }

  if (rule.type === STYLE_RULE) {
    const selector = scopeSelectorList(rule.selectorText, scopeSelector);
    const body = rule.style ? rule.style.cssText : '';

    // The declarations as the CSS parser itself read them, which is the one
    // reading no string, comment or brace trick can mislead.
    if (isDangerousValue(body)) {
      return '';
    }

    return selector && body ? `${selector} { ${body} }` : '';
  }

  if (rule.type === MEDIA_RULE || rule.type === SUPPORTS_RULE) {
    const inner = serialiseRules(rule.cssRules, scopeSelector);

    if (!inner) {
      return '';
    }

    const condition = rule.conditionText || (rule.media && rule.media.mediaText) || '';
    const at = rule.type === MEDIA_RULE ? '@media' : '@supports';

    return `${at} ${condition} { ${inner} }`;
  }

  // `@font-face` and `@keyframes` name resources and animations rather than
  // matching elements, so there is nothing in them to scope.
  if (rule.type === FONT_FACE_RULE || rule.type === KEYFRAMES_RULE) {
    const css = rule.cssText || '';

    return isDangerousValue(css) ? '' : css;
  }

  // Anything else — `@import` above all, which would pull in a stylesheet we
  // never get to inspect — is dropped.
  return '';
}

// The CSSOM is the only thing that reads CSS the way the browser about to
// apply it does. A `<style>` inside a document of its own is the first choice;
// engines that only build a stylesheet for a style element in *the* document
// get one that can never apply, because `media="not all"` matches nothing.
function readCssRules(css, handler) {
  if (typeof document === 'undefined' || !document.createElement) {
    return null;
  }

  const documents = [];

  if (document.implementation && document.implementation.createHTMLDocument) {
    try {
      documents.push(document.implementation.createHTMLDocument(''));
    } catch (error) {
      // An engine without a working `createHTMLDocument` falls through.
    }
  }

  documents.push(document);

  for (let index = 0; index < documents.length; index += 1) {
    const doc = documents[index];
    const host = doc.head || doc.documentElement || doc.body;
    let style = null;

    try {
      style = doc.createElement('style');
      style.setAttribute('media', 'not all');
      style.appendChild(doc.createTextNode(css));
      host.appendChild(style);

      if (style.sheet && style.sheet.cssRules) {
        return handler(style.sheet.cssRules);
      }
    } catch (error) {
      // Fall through to the next document, then to the caller's fallback.
    } finally {
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }
  }

  return null;
}

// Returns the rewritten stylesheet, or `null` when it could not be parsed —
// which the caller treats as "drop it", because an unparsed stylesheet cannot
// be confined to the preview.
function scopeStylesheet(css, scopeSelector) {
  if (!String(css || '').trim()) {
    return '';
  }

  return readCssRules(css, rules => serialiseRules(rules, scopeSelector));
}

function stripAttributes(element) {
  // `attributes` is live, so collect the names before removing anything.
  const names = Array.prototype.map.call(element.attributes, attribute => attribute.name);

  names.forEach(name => {
    const lowered = name.toLowerCase();

    // Every inline event handler: onclick, onerror, onload, …
    if (lowered.indexOf('on') === 0) {
      element.removeAttribute(name);
      return;
    }

    if (URL_ATTRIBUTES.indexOf(lowered) !== -1 && isDangerousUrl(element.getAttribute(name))) {
      element.removeAttribute(name);
      return;
    }

    if (SRCSET_ATTRIBUTES.indexOf(lowered) !== -1 && isDangerousSrcset(element.getAttribute(name))) {
      element.removeAttribute(name);
    }
  });
}

function cleanIframe(iframe) {
  // An iframe that carries its own document is a script host in disguise.
  iframe.removeAttribute('srcdoc');

  const source = iframe.getAttribute('src') || '';

  // Only https embeds survive; anything else (about:, data:, a relative path
  // that would resolve against the widget's own origin) is dropped.
  if (!/^https:\/\//i.test(normaliseUrl(source))) {
    remove(iframe);
    return;
  }

  // Set last, after `stripAttributes`, so a vendor's own wider `sandbox` (or
  // no sandbox at all) is replaced rather than merged with.
  iframe.setAttribute('sandbox', IFRAME_SANDBOX);
}

function cleanStyleElement(element, scopeSelector) {
  // A stylesheet inside <svg> is foreign content, i.e. the same re-parsing
  // trick as MathML.
  if (isInNamespace(element, SVG_NAMESPACE) || hasDangerousStylesheet(element.textContent)) {
    remove(element);
    return;
  }

  if (!scopeSelector) {
    return;
  }

  const scoped = scopeStylesheet(element.textContent, scopeSelector);

  if (scoped === null) {
    remove(element);
    return;
  }

  if (scoped !== element.textContent) {
    element.textContent = scoped;
  }
}

function cleanElement(element, scopeSelector) {
  const name = localNameOf(element);

  if (BLOCKED_ELEMENTS.indexOf(name) !== -1) {
    remove(element);
    return;
  }

  if (MATHML_ELEMENTS.indexOf(name) !== -1 || isInNamespace(element, MATHML_NAMESPACE)) {
    remove(element);
    return;
  }

  if (element.hasAttribute && element.hasAttribute('style')
    && hasDangerousDeclaration(element.getAttribute('style'))) {
    remove(element);
    return;
  }

  if (UNWRAPPED_ELEMENTS.indexOf(name) !== -1) {
    stripAttributes(element);
    unwrap(element);
    return;
  }

  if (name === 'style') {
    cleanStyleElement(element, scopeSelector);

    if (!element.parentNode) {
      return;
    }
  }

  stripAttributes(element);

  if (name === 'iframe') {
    cleanIframe(element);
  }
}

function cleanTree(root, scopeSelector) {
  Array.prototype.slice.call(root.querySelectorAll('*'))
    .forEach(element => cleanElement(element, scopeSelector));
}

function sanitisePass(html, scopeSelector) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  if (!doc || !doc.body) {
    return '';
  }

  cleanTree(doc, scopeSelector);

  // A swipe is often a whole document, whose <style> block lives in <head>;
  // dropping it would preview the copy without any of its styling. Move what
  // survived to the front of the body instead.
  const headStyles = doc.head
    ? Array.prototype.map.call(doc.head.querySelectorAll('style'), style => style.outerHTML).join('')
    : '';

  return headStyles + doc.body.innerHTML;
}

// `DOMParser` and `innerHTML` are two different parsers: the first runs with
// scripting off, the second — the one `v-html` uses — with scripting on. Markup
// that is stable under the first and hostile under the second is the whole
// noscript/mXSS family, so the candidate is finally handed to the live parser,
// sanitised again there, and only accepted when that changes nothing either.
function liveRoundTrip(html, scopeSelector) {
  if (typeof document === 'undefined' || !document.createElement) {
    return null;
  }

  const host = document.createElement('div');

  host.innerHTML = html;
  cleanTree(host, scopeSelector);

  return host.innerHTML;
}

export function sanitiseHtml(html, options) {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Server-side rendering or a test environment without a DOM: render nothing
  // rather than pass the markup through unchecked.
  if (typeof DOMParser === 'undefined') {
    return '';
  }

  const scope = typeof options === 'string' ? options : (options && options.scope) || '';
  const scopeSelector = scope ? `.${String(scope).trim()}` : '';

  // mXSS turns on the difference between what one parser builds and what the
  // other builds from the same markup, so sanitising once is not enough: keep
  // going until a pass — through both parsers — changes nothing. Markup that
  // will not settle is not markup a promo asset needs; show it as text.
  let current = html;

  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    const parsed = sanitisePass(current, scopeSelector);
    const live = liveRoundTrip(parsed, scopeSelector);

    if (live === null) {
      if (parsed === current) {
        return current;
      }

      current = parsed;
    } else {
      if (parsed === current && live === parsed) {
        return current;
      }

      current = live;
    }
  }

  return escapeHtml(html);
}

export default sanitiseHtml;
